import { Auth } from 'aws-amplify';
import { QueryClient, UseMutationResult } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods, _Payload, _User, _UserType } from '../constants/staticTypes';
import {
    changePasswordApi,
    forgotPasswordApi,
    getAdminDataByUserNameApi,
    getCognitoUserNameApi,
    getOrgDataByUserNameApi,
    orgStaffChangePasswordApi,
    resetPasswordApi,
    updateAdminStaffEmailIdGetOtpApi,
    updateOrgProfilePictureApi,
    updateOrgStaffEmailIdApi,
    updateOrgStaffEmailIdGetOtpApi,
    updateProfilePictureApi,
} from '../constants/apiEndPoints';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';
import { PERMISSIONS } from '../constants/queryKeys';
import * as Yup from 'yup';

export type UserLogin = {
    username: string;
    password: string;
};

// user authentication
export const userLogin = (userDetails: UserLogin) => Auth.signIn(userDetails.username?.trim(), userDetails.password);

// user session
export const userSession = () => Auth.currentAuthenticatedUser({ bypassCache: false });

// user custom attributes - one Cognito GetUser call for all requested attributes, values in request order
export const userCustomAttributeValues = async (user: any, attributes: string[]) => {
    const userAttributes = await Auth.userAttributes(user);
    return attributes.map((attribute) => userAttributes.find((attr: any) => attr.Name === attribute)?.Value ?? null);
};

export const getUserName = (userType: _UserType, username: string) => {
    if (userType === 'superAdmin' || userType === 'adminStaff') {
        return username;
    }
    return `${username}#organization`;
};

// user logout
export const userLogout = async () => {
    await Auth.signOut();
    localStorage.clear();
};

// check cognito data and clear
export const clearCognitoData = () => {
    const cognitoRegex = /CognitoIdentityServiceProvider/;
    const checkIfCognitoDetails = Object.keys(localStorage);
    const checkIfCognitoDetailsPresent = checkIfCognitoDetails?.some((value) => cognitoRegex?.test(value));
    if (checkIfCognitoDetailsPresent) {
        localStorage.clear();
    }
};

// get user info and assigned permissions
export const getUserDetailsByUserName = (queryClient: QueryClient, userType: _UserType, userName: string) => {
    return HttpServiceHelper({
        url: userType === 'superAdmin' || userType === 'adminStaff' ? getAdminDataByUserNameApi : getOrgDataByUserNameApi,
        method: _HttpMethods.POST,
        queryClient,
        data: {
            userName,
        },
    });
};

// get cognito user-name
export const getCognitoUserName = (queryClient: QueryClient, userName: string) => {
    return HttpServiceHelper({
        queryClient,
        url: getCognitoUserNameApi,
        method: _HttpMethods.POST,
        noAuth: true,
        data: {
            userName,
        },
    });
};

export const forgotPasswordWithEmail = (
    forgotPasswordWithEmailMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: { email: string },
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    forgotPasswordWithEmailMutation.mutate(
        {
            url: forgotPasswordApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _ResetNewPassword = {
    userName: string;
    code: string;
    password: string;
    isAcceptedPrivacyPolicy: boolean;
};

export const resetNewPassword = (
    resetNewPasswordMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _ResetNewPassword,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    resetNewPasswordMutation.mutate(
        {
            url: resetPasswordApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

// set permissions
export const setPermissions = (permissions: { [key: string]: any }) => ({ permissions });

//save permissions
export const savePermissions = (queryClient: QueryClient, permissions: { [key: string]: any }) => {
    queryClient.setQueryData(PERMISSIONS, setPermissions(permissions));
    const permissionString = JSON.stringify(permissions);
    localStorage.setItem(PERMISSIONS, permissionString);
};

// get user permissions
export const getUserPermissions = (userInfo: _User) => {
    type PermissionsObject = {
        [key: string]: any;
    };

    let permissions: PermissionsObject = {};

    // modified permission structure based on updated user permissions
    userInfo?.staffPermissionRoles[0]?.permissions?.forEach((permission: any) => {
        if (permission?.permissionKey) {
            const key = permission.permissionKey;
            const isActive = permission.isActive;
            const values = permission.permissions
                .map((subPermission: any) => {
                    if (subPermission.isActive) {
                        return subPermission.permissionKey;
                    }
                    return null;
                })
                .filter((key: any) => key !== null);

            // if parent permission is active or at-least one sub-permission is active, set permission as active
            if (isActive || values.length > 0) {
                permissions[key] = values;
            }
        }
    });
    return permissions;
};

export type _ChangePasswordForm = {
    oldPassword: string;
    password: string;
    confirmPassword: string;
};

export const initialChangePasswordFormData: _ChangePasswordForm = {
    oldPassword: '',
    password: '',
    confirmPassword: '',
};

export const ChangePasswordSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Old password is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
        .required('Password is required'),
    confirmPassword: Yup.string()
        .required('Confirm password is required')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
});

export type _ChangePassword = {
    staffId: string;
    newPassword: string;
    oldPassword: string;
    confirmPassword: string;
};

export const changeNewPassword = (
    changePasswordMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _ChangePassword,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    changePasswordMutation.mutate(
        {
            url: changePasswordApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const orgStaffChangeNewPassword = (
    orgStaffChangePasswordMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _ChangePassword,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    orgStaffChangePasswordMutation.mutate(
        {
            url: orgStaffChangePasswordApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _UpdateProfilePicture = {
    staffId: string;
    photoKey: string;
};

export const updateProfilePicture = (
    updateProfileMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateProfilePicture,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateProfileMutation.mutate(
        {
            url: updateProfilePictureApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const updateOrgProfilePicture = (
    updateOrgProfileMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateProfilePicture,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateOrgProfileMutation.mutate(
        {
            url: updateOrgProfilePictureApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _GetOtpForEmailUpdate = {
    staffId: string;
    email: string;
};

export const getOtpForEmailUpdateOrgStaff = (
    getOtpMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _GetOtpForEmailUpdate,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getOtpMutation.mutate(
        {
            url: updateOrgStaffEmailIdGetOtpApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _UpdateOrgStaffEmailId = {
    staffId: string;
    oldEmail: string;
    newEmail: string;
    code: string;
};

export const updateOrgStaffEmailId = (
    updateOrgStaffEmailIdMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateOrgStaffEmailId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateOrgStaffEmailIdMutation.mutate(
        {
            url: updateOrgStaffEmailIdApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const getOtpForEmailUpdateAdminStaff = (
    getOtpMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _GetOtpForEmailUpdate,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getOtpMutation.mutate(
        {
            url: updateAdminStaffEmailIdGetOtpApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const updateAdminStaffEmailId = (
    updateAdminStaffEmailIdMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateOrgStaffEmailId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateAdminStaffEmailIdMutation.mutate(
        {
            url: updateOrgStaffEmailIdApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
